import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { testIds } from '@/lib/test-ids';
import type { QuizRun } from '@/lib/api/quiz';

interface QuizRunsTabProps {
  runs: QuizRun[];
  loading: boolean;
  bestScore: number;
  trend: number;
  passingScore: number;
  onSelectRun?: (runId: string) => void;
}

function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return <span className="text-xs text-muted-foreground">-</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 48;
  const h = 16;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} className="inline-block">
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="text-primary"
        points={points}
      />
    </svg>
  );
}

function formatRelativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
}

export function QuizRunsTab({
  runs,
  loading,
  bestScore,
  trend,
  passingScore,
  onSelectRun,
}: QuizRunsTabProps) {
  if (loading) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  const scoredRuns = runs.filter((r) => r.score_pct != null);
  const scores = scoredRuns.map((r) => Number(r.score_pct));
  const allSurvey = runs.length > 0 && scoredRuns.length === 0;

  return (
    <div className="h-full min-h-0 overflow-y-auto p-5 space-y-4" data-testid={testIds.quizDocumentRenderer.runsTab}>
      <div className="flex items-center gap-4 text-sm flex-wrap">
        {allSurvey ? (
          <span className="text-muted-foreground">{runs.length} survey run{runs.length === 1 ? '' : 's'}</span>
        ) : (
          <>
            <span>
              Best: <strong className="tabular-nums">{bestScore}%</strong>
            </span>
            <span>
              Trend:{' '}
              <strong
                className={cn(
                  'tabular-nums',
                  trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : ''
                )}
              >
                {trend > 0 ? '+' : ''}
                {trend}%
              </strong>
            </span>
            <span className="text-muted-foreground">Passing: {passingScore}%</span>
          </>
        )}
      </div>

      {runs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No completed runs yet.</p>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-left">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2 text-right">Score</th>
                <th className="px-3 py-2 text-right">Trend</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run, i) => {
                const isSurveyRun = run.score_pct == null;
                const pct = isSurveyRun ? null : Number(run.score_pct);
                const slice = scores.length >= 2 ? scores.slice(Math.max(0, scoredRuns.length - (runs.length - i))).reverse() : [];
                const clickable = !!onSelectRun && !isSurveyRun;
                return (
                  <tr
                    key={run.id}
                    className={cn(
                      'border-t border-border',
                      clickable && 'cursor-pointer hover:bg-muted/40'
                    )}
                    data-testid={testIds.quizDocumentRenderer.runRow(run.id)}
                    onClick={clickable ? () => onSelectRun(run.id) : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onSelectRun(run.id);
                            }
                          }
                        : undefined
                    }
                    tabIndex={clickable ? 0 : undefined}
                    role={clickable ? 'button' : undefined}
                  >
                    <td className="px-3 py-2 text-muted-foreground">
                      {run.completed_at ? formatRelativeTime(run.completed_at) : '-'}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right tabular-nums font-medium',
                        isSurveyRun
                          ? 'text-muted-foreground'
                          : run.passed
                            ? 'text-green-600'
                            : 'text-red-600'
                      )}
                    >
                      {isSurveyRun ? 'Survey' : `${pct}%`}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isSurveyRun ? (
                        <span className="text-xs text-muted-foreground">-</span>
                      ) : (
                        <Sparkline values={slice} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {onSelectRun && (
            <p className="text-xs text-muted-foreground px-3 py-2 border-t border-border">
              Click a run to review answers
            </p>
          )}
        </div>
      )}
    </div>
  );
}
